/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import React, {useEffect, useMemo} from 'react';
import Helmet from 'react-helmet';
import PropTypes from 'prop-types';
import {useLocation} from 'react-router-dom';

import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';

import {trackPageViewFromSeo} from '@amblique/king-living/app/analytics/ga4/page-context';
import {DEFAULT_SITE_TITLE} from '@amblique/king-living/app/constants';
import {jsxParser} from '@amblique/base/app/helpers/jsx-parser';
import {useSitePreference} from '@amblique/base/app/hooks/use-site-preference';
import {
  isHomePagePathname,
  mergeSeoSources,
  resolveCanonicalUrl,
  resolveRegionalSeoLinks,
  resolveRobotsContent,
  resolveTitleForDocument,
} from '@amblique/king-living/app/utils/seo-utils';

export {resolveCanonicalUrl} from '@amblique/king-living/app/utils/seo-utils';

function decodeHtmlHeadPreference(value) {
  if (value == null || typeof value !== 'string') return null;
  const trimmed = value.trim();

  if (!trimmed) return null;
  try {
    return atob(trimmed);
  } catch {
    return trimmed.includes('<') ? trimmed : null;
  }
}

/**
 * @override
 *  - Use King Living site title instead of the Retail React App default
 *  - Inject `htmlHead` from site preferences
 *  - Support Contentful SEO data via `contentfulSeo` prop
 *  - (Typekit fonts now load once, globally, from `components/_app` — KINDW-247)
 */
const Seo = ({
  children,
  contentfulSeo,
  description,
  jsonLdGraph,
  keywords,
  noIndex,
  ogImage,
  ogType = 'website',
  productMeta,
  regionalSeoLinks = false,
  title,
  ...props
}) => {
  const merged = useMemo(
    () => mergeSeoSources({description, keywords, ogImage, title}, contentfulSeo),
    [contentfulSeo, description, keywords, ogImage, title]
  );

  const location = useLocation();
  const isHomepage = isHomePagePathname(location.pathname);
  const hasContentfulSeoTitle = Boolean(contentfulSeo?.title?.trim());

  const resolvedTitle = merged.title ?
    resolveTitleForDocument({
      hasContentfulSeoTitle,
      isHomepage,
      siteTitle: DEFAULT_SITE_TITLE,
      title: merged.title,
    }) :
    DEFAULT_SITE_TITLE;
  const ogTitle = merged.title || resolvedTitle;

  const countries = useSitePreference('countries');
  const htmlHead = useSitePreference('htmlHead');
  const brandLogo = useSitePreference('brandLogo');
  const appOrigin = useAppOrigin();
  const {locale, site} = useMultiSite();
  const isAppShellOnly = regionalSeoLinks && !merged.title && !contentfulSeo?.title?.trim();

  const robots = resolveRobotsContent({noIndex, pathname: location.pathname});
  const isIndexable = robots === 'INDEX,FOLLOW';
  const canonicalUrl = resolveCanonicalUrl({
    appOrigin,
    countries,
    countryCode: site?.countryCode,
    noIndex,
    pathname: location.pathname,
  });
  const resolvedOgImage = merged.ogImage || ogImage || brandLogo || null;

  const regionalSeoHeadLinks = useMemo(() => {
    const data = resolveRegionalSeoLinks({
      appOrigin,
      countries,
      countryCode: site?.countryCode,
      noIndex,
      pathname: location.pathname,
      regionalSeoLinks,
    });

    if (!data) return null;

    return [
      <link key="seo-canonical" href={data.canonicalUrl} rel="canonical" />,
      <link key="seo-x-default" href={data.canonicalUrl} hrefLang="x-default" rel="alternate" />,
      ...data.alternates.map(({href, hreflang}) => (
        <link key={hreflang} href={href} hrefLang={hreflang} rel="alternate" />
      ))
    ];
  }, [appOrigin, countries, location.pathname, noIndex, regionalSeoLinks, site?.countryCode]);

  const openGraphTags = useMemo(() => {
    if (!isIndexable) return [];

    const tags = [
      <meta key="og:type" content={ogType} property="og:type" />,
      <meta key="og:title" content={ogTitle} property="og:title" />
    ];

    if (merged.description) {
      tags.push(
        <meta key="og:description" content={merged.description} property="og:description" />
      );
    }

    if (canonicalUrl) {
      tags.push(<meta key="og:url" content={canonicalUrl} property="og:url" />);
    }

    if (resolvedOgImage) {
      tags.push(<meta key="og:image" content={resolvedOgImage} property="og:image" />);
    }

    if (ogType === 'product' && productMeta) {
      if (productMeta.brand) {
        tags.push(
          <meta key="product:brand" content={productMeta.brand} property="product:brand" />
        );
      }

      if (productMeta.availability) {
        tags.push(
          <meta
            key="product:availability"
            content={productMeta.availability}
            property="product:availability"
          />
        );
      }

      if (productMeta.condition) {
        tags.push(
          <meta key="product:condition" content={productMeta.condition} property="product:condition" />
        );
      }

      if (productMeta.retailerItemId) {
        tags.push(
          <meta
            key="product:retailer_item_id"
            content={productMeta.retailerItemId}
            property="product:retailer_item_id"
          />
        );
      }

      if (productMeta.priceAmount) {
        tags.push(
          <meta
            key="product:price:amount"
            content={productMeta.priceAmount}
            property="product:price:amount"
          />
        );
      }

      if (productMeta.currency) {
        tags.push(
          <meta key="product:price:currency" content={productMeta.currency} property="product:price:currency" />
        );
      }
    }

    return tags;
  }, [
    canonicalUrl,
    isIndexable,
    merged.description,
    ogTitle,
    ogType,
    productMeta,
    resolvedOgImage
  ]);

  const parsedHead = useMemo(() => {
    const decoded = decodeHtmlHeadPreference(htmlHead);

    if (!decoded) return null;
    try {
      return jsxParser.parse(decoded);
    } catch {
      return null;
    }
  }, [htmlHead]);

  const jsonLdScript = useMemo(() => {
    if (!isIndexable || !jsonLdGraph?.['@graph']?.length) return null;

    const serialized = JSON.stringify(jsonLdGraph).replace(/</g, '\\u003c');

    return (
      <script key="json-ld-graph" type="application/ld+json">
        {serialized}
      </script>
    );
  }, [isIndexable, jsonLdGraph]);

  useEffect(() => {
    if (isAppShellOnly) {
      return;
    }

    trackPageViewFromSeo({
      country: site?.countryCode,
      currency: locale?.preferredCurrency,
      language: locale?.id,
      pagePath: `${location.pathname}${location.search}`,
      pageTitle: resolvedTitle,
    });
  }, [
    isAppShellOnly,
    locale?.id,
    locale?.preferredCurrency,
    location.pathname,
    location.search,
    resolvedTitle,
    site?.countryCode
  ]);

  return (
    <Helmet {...props}>
      <title>{resolvedTitle}</title>
      {merged.title && <meta content={resolvedTitle} name="title" />}
      {merged.description && <meta content={merged.description} name="description" />}
      {merged.keywords && <meta content={merged.keywords} name="keywords" />}
      <meta content={robots} name="robots" />
      {openGraphTags}
      {regionalSeoHeadLinks}
      {jsonLdScript}
      {children}
      {parsedHead}
      {/* KINDW-247 — Typekit font stylesheet moved to the single global source in
          `components/_app` (with preconnect). Do not re-add it here. */}
    </Helmet>
  );
};

Seo.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  noIndex: PropTypes.bool,
  ogType: PropTypes.oneOf(['website', 'article', 'product']),
  ogImage: PropTypes.string,
  regionalSeoLinks: PropTypes.bool,
  children: PropTypes.node,
  keywords: PropTypes.string,
  productMeta: PropTypes.shape({
    availability: PropTypes.string,
    brand: PropTypes.string,
    condition: PropTypes.string,
    currency: PropTypes.string,
    priceAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    retailerItemId: PropTypes.string,
  }),
  contentfulSeo: PropTypes.shape({
    description: PropTypes.string,
    keywords: PropTypes.string,
    ogImage: PropTypes.string,
    title: PropTypes.string,
  }),
  jsonLdGraph: PropTypes.shape({
    '@context': PropTypes.string,
    '@graph': PropTypes.arrayOf(PropTypes.object),
  }),
};

export default Seo;
